/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import * as React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { t } from "i18n";
import Lane from "components/lane/lane";
import PlusCircle from "components/icons/plus-circle";
import SortedCollectionCard from "pages/collections/components/sorted-collection-card/sorted-collection-card";
import { COLLECTIONS_ALL_ITEMS_PATH } from "../../routes";
import { actions } from "../../actions";
import {
  toggleCollectionItemAction,
  expandCollectionCardAction,
  updateSortedItemsAction,
  updateAssociatedMetadataAction
} from "../../async-action-creators";
import { navigateFn } from "../../../../utils/routes.utils";
import { selectIsDesktopSizeViewport } from "store/viewport";
import styles from "./sorted-items.module.css";

const isSelected = (item, sortedItems) => {
  if (sortedItems.find((i) => i.id === item.id)) {
    return true;
  } else {
    return false;
  }
};

const swapItems = (items, firstItem, afterItem, updateSwappedItems, updateSortedItems, initialSwappedItems) => {
  if (firstItem === afterItem) return;
  const itemIndex = items.indexOf(firstItem);
  const afterIndex = items.indexOf(afterItem);
  const swappedItems = [...items];
  const [movingItem] = swappedItems.splice(itemIndex, 1);
  swappedItems.splice(afterIndex, 0, movingItem);

  if (initialSwappedItems !== swappedItems) {
    updateSwappedItems(swappedItems);
    updateSortedItems(swappedItems);
  }
};

const SortedItems = ({
  items,
  toggleCollectionItem,
  swappedItems,
  updateSortedItems,
  updateSwappedItems,
  changePageViewsThreshold,
  updatePageViewsThreshold,
  updateStagingAssociatedMetadata,
  currentCardExpandedIndex,
  toggleCardExpanded,
  stagingAssociatedMetadata,
  changeAssociatedMetadataLayout,
  cancelCardExpanded,
  updateAssociatedMetadata,
  templateOptions,
  isAssociatedMetadataEnabled,
  navigate,
  threshold,
  expandCollectionCard,
  attributes,
  hasCollectionRankNestedCollectionPermission,
  storyLimit,
  storiesPageViews,
  stagingSelectedCollection,
  openAllItemsInspector,
  isDesktopSizeViewport
}) => (
  <DndProvider backend={HTML5Backend}>
    <div className={styles["collection-lane"]}>
      <Lane
        title={
          t("collections.sorted_items") +
          (isDesktopSizeViewport ? "" : `\u00A0\u00A0\u00A0\u00A0${items.length}/${storyLimit}`)
        }
        attributes={() =>
          isDesktopSizeViewport ? (
            `${items.length}/${storyLimit}`
          ) : (
            <div
              onClick={() => {
                openAllItemsInspector(stagingSelectedCollection.id);
              }}
              className={styles["open-collection-inspector-button"]}
              data-test-id="open-collection-inspector-button">
              <PlusCircle />
            </div>
          )
        }>
        <div className={styles["collection-item-list"]} data-test-id="collection-items-list-rhs">
          {items &&
            items.map((item, index) => {
              return (
                <SortedCollectionCard
                  key={item.id}
                  item={item}
                  pageViews={storiesPageViews && storiesPageViews[item.id]}
                  isSelected={isSelected(item, items)}
                  toggleCollectionItem={toggleCollectionItem}
                  onDragOver={(firstItem, secondItem) =>
                    swapItems(items, firstItem, secondItem, updateSwappedItems, updateSortedItems, swappedItems)
                  }
                  onDragEnd={() => swappedItems && updateSortedItems(swappedItems)}
                  canDrag={items.length > 1}
                  badge={index + 1}
                  index={index}
                  type="sorted"
                  changePageViewsThreshold={changePageViewsThreshold}
                  updatePageViewsThreshold={updatePageViewsThreshold}
                  updateStagingAssociatedMetadata={updateStagingAssociatedMetadata}
                  updateAssociatedMetadata={updateAssociatedMetadata}
                  changeAssociatedMetadataLayout={changeAssociatedMetadataLayout}
                  isCardExpanded={currentCardExpandedIndex === index}
                  toggleCardExpanded={toggleCardExpanded}
                  stagingAssociatedMetadata={stagingAssociatedMetadata}
                  cancelCardExpanded={cancelCardExpanded}
                  templateOptions={templateOptions}
                  isAssociatedMetadataEnabled={isAssociatedMetadataEnabled}
                  navigate={navigate}
                  threshold={threshold}
                  expandCollectionCard={expandCollectionCard}
                  attributes={attributes}
                  hasCollectionRankNestedCollectionPermission={hasCollectionRankNestedCollectionPermission}
                />
              );
            })}
        </div>
      </Lane>
    </div>
  </DndProvider>
);

const mapStateToProps = (state, ownProps) => {
  const { collections, features, config } = state;
  return {
    items: collections.collectionPage.selectedCollection.items,
    storiesPageViews: collections.storiesPageViews,
    swappedItems: collections.ui.swappedItems,
    currentCardExpandedIndex: collections.ui.currentCardExpandedIndex,
    stagingAssociatedMetadata: collections.ui.stagingAssociatedMetadata,
    templateOptions: ownProps.templateOptions,
    isAssociatedMetadataEnabled: features.associatedMetadata,
    threshold: collections.ui.threshold,
    attributes: ownProps.attributes,
    hasCollectionRankNestedCollectionPermission: features.hasCollectionRankNestedCollectionPermission,
    storyLimit: config.collectionStoryLimit,
    stagingSelectedCollection: collections.collectionPage.stagingSelectedCollection,
    isDesktopSizeViewport: selectIsDesktopSizeViewport(state)
  };
};

const mapDispatchToProps = (dispatch) => {
  const navigate = navigateFn(dispatch);
  return {
    navigate,
    toggleCollectionItem: (item) => dispatch(toggleCollectionItemAction(item)),
    updateSwappedItems: (items) => dispatch({ type: actions.UPDATE_SWAPPED_ITEMS, payload: { items } }),
    updateSortedItems: (items) => {
      dispatch(updateSortedItemsAction(items));
    },
    changePageViewsThreshold: (data) => dispatch({ type: actions.CHANGE_PAGE_VIEWS, payload: { data } }),
    updatePageViewsThreshold: (index) => dispatch({ type: actions.UPDATE_PAGE_VIEWS, payload: { index } }),
    updateStagingAssociatedMetadata: (name, value) =>
      dispatch({ type: actions.UPDATE_STAGING_ASSOCIATED_METADATA, payload: { key: name, value } }),
    toggleCardExpanded: (index, item) => dispatch(expandCollectionCardAction(item, index)),
    changeAssociatedMetadataLayout: (value, options) =>
      dispatch({ type: actions.CHANGE_COLLECTION_LAYOUT, payload: { value, options } }),
    cancelCardExpanded: (index, key, value) =>
      dispatch({ type: actions.CANCEL_CARD_EXPAND, payload: { index, key, value } }),
    updateAssociatedMetadata: (index, key, value) => {
      dispatch(updateAssociatedMetadataAction(index, key, value));
    },
    expandCollectionCard: (item, index) => dispatch(expandCollectionCardAction(item, index)),
    openAllItemsInspector: (collectionId) => {
      document.body.className = "prevent-normal-pull-to-refresh";
      navigate(COLLECTIONS_ALL_ITEMS_PATH, { collectionId: collectionId || "new" });
    }
  };
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(SortedItems);
